import React, { useRef, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  BooleanParam,
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import { UseTranslationResponse } from 'react-i18next';
import clsx from 'clsx';
import formStyles from '../../../components/form/form.module.scss';
import { DeviceOverviewQuery } from 'services/api/api-client.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import styles from './DeviceOverview.module.scss';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import {
  abasUnavailableUrn,
  ErrObj,
  formatDate,
} from '../../../helpers/abas-helper.ts';
import { MenuItem, Select } from '@mui/material';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { useWatch } from 'react-hook-form';
import { add, sub } from 'date-fns';
import { TableHeaderCell } from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';

type Form = {
  serialNumber: string;
  periodType: string;
  amountOfPeriods: number | null;
  periodDirection: string;
  maintenanceFrom: Date;
  maintenanceTo: Date;
};

const next = 'next';
const last = 'last';

const pageSizes = [10, 20, 50];
export const DeviceOverview = () => {
  const i18n = useScopedTranslation('Page.DeviceOverview');
  const [isPeriodSelectVisible, setIsPeriodSelectVisible] = useState(false);
  const [isDirectionSelectVisible, setIsDirectionSelectVisible] =
    useState(false);

  const directionOptions = [next, last];
  const periodOptions = ['year', 'month', 'week'];

  const [params, setParams] = useQueryParams({
    serialNumber: StringParam,
    periodType: StringParam,
    periodDirection: StringParam,
    amountOfPeriods: NumberParam,
    maintenanceFrom: DateParam,
    maintenanceTo: DateParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'nextMaintenance'),
    desc: withDefault(BooleanParam, false),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      let maintenanceFrom: Date | undefined =
        periodDirection === last && amountOfPeriods
          ? sub(new Date(), {
              [periodType + 's']: amountOfPeriods,
            })
          : new Date();

      let maintenanceTo: Date | undefined =
        periodDirection === next && amountOfPeriods
          ? add(new Date(), {
              [periodType + 's']: amountOfPeriods,
            })
          : new Date();

      if (!amountOfPeriods) {
        maintenanceFrom = undefined;
        maintenanceTo = undefined;
      }

      setParams({
        ...params,
        ...data,
        page: 1,
        maintenanceFrom,
        maintenanceTo,
      });
    },
    {
      defaultValues: {
        serialNumber: params.serialNumber ?? '',
        amountOfPeriods: params.amountOfPeriods ?? null,
        periodType: params.periodType ?? periodOptions[0],
        periodDirection: params.periodDirection ?? directionOptions[0],
      },
    },
  );

  const [periodType, periodDirection, amountOfPeriods, serialNumber] = useWatch(
    {
      control: form.control,
      name: [
        'periodType',
        'periodDirection',
        'amountOfPeriods',
        'serialNumber',
      ],
    },
  );

  const deviceData = DeviceOverviewQuery.useGetQuery(
    {
      serialNumber: params.serialNumber?.trim(),
      maintenanceFrom: params.maintenanceFrom,
      maintenanceTo: params.maintenanceTo,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const data = deviceData.data;
  const numberOfRows = data?.records?.length ?? 0;
  const totalCount = data?.countOfRecords ?? 0;

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const columnsConfig = getColumnsConfig(i18n);
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: totalCount,
    pageSizes: pageSizes,
    numberOfRows: numberOfRows,
    changePagination: changePagination,
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const resetFilters = () => {
    form.reset({
      serialNumber: '',
      amountOfPeriods: null,
      periodType: periodOptions[0],
      periodDirection: directionOptions[0],
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShow =
    !!params.serialNumber || !!params.maintenanceFrom || !!params.maintenanceTo;

  const errObj = deviceData?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !data?.records?.length &&
    !deviceData.isLoading &&
    !deviceData.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'device-overview-container'}>
      <Breadcrumbs />
      <Loading loading={deviceData.isLoading || deviceData.isPlaceholderData}>
        <form onSubmit={form.handleSubmitDefault}>
          <div className={clsx(styles.rowContainer, styles.columnGap25)}>
            <Input
              testId={'serial-number-input'}
              {...form.register('serialNumber')}
              leftHelperText={i18n.t('serial_number')}
              className={styles.width300}
              endAdornment={
                <div>
                  {serialNumber && (
                    <IconButton
                      type={'destroy'}
                      onClick={() => form.setValue('serialNumber', '')}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.endAdornmentClassname}
            />
            <div className={clsx(styles.rowContainer, styles.columnGap8)}>
              <div className={styles.greyText}>
                {i18n.t('next_maintenance')}
              </div>
              <Select
                data-test-id={'direction-select'}
                open={isDirectionSelectVisible}
                onClick={() => {
                  setIsDirectionSelectVisible(true);
                }}
                onClose={(e) => {
                  e.stopPropagation();
                  setIsDirectionSelectVisible(false);
                }}
                value={periodDirection}
                onChange={(e) =>
                  form.setValue('periodDirection', e.target.value)
                }
                className={clsx(formStyles.select, styles.width90)}
                IconComponent={ArrowDown}
                MenuProps={{
                  PaperProps: {
                    className: clsx(formStyles.selectBody, styles.width90),
                  },
                  disableScrollLock: true,
                }}
              >
                {directionOptions.map((directionOption) => (
                  <MenuItem key={directionOption} value={directionOption}>
                    {tDynamic(i18n, directionOption)}
                  </MenuItem>
                ))}
              </Select>
              <Input
                testId={'time-interval-input'}
                className={styles.width84}
                containerClassName={styles.marginBottomMinus4}
                {...form.register('amountOfPeriods')}
                type={'number'}
                endAdornment={
                  <div className={styles.marginTopMinus3}>
                    {amountOfPeriods && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => form.setValue('amountOfPeriods', null)}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={styles.endAdornmentClassname}
              />
              <Select
                open={isPeriodSelectVisible}
                onClick={() => {
                  setIsPeriodSelectVisible(true);
                }}
                onClose={(e) => {
                  e.stopPropagation();
                  setIsPeriodSelectVisible(false);
                }}
                value={periodType}
                onChange={(e) => form.setValue('periodType', e.target.value)}
                className={formStyles.select}
                IconComponent={ArrowDown}
                MenuProps={{
                  PaperProps: {
                    className: formStyles.selectBody,
                  },
                  disableScrollLock: true,
                }}
              >
                {periodOptions.map((periodOption) => (
                  <MenuItem key={periodOption} value={periodOption}>
                    {tDynamic(i18n, periodOption)}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <Button
              type={'submit'}
              className={clsx(styles.actionButton, styles.marginBottomMinus4)}
              title={i18n.t('apply')}
              testId={'filters-apply-button'}
            />
            {isResetButtonShow && (
              <NegativeActionText
                text={i18n.t('reset')}
                onClickHandler={resetFilters}
                containerClassName={styles.marginBottomMinus4}
                testId={'filters-reset-button'}
              />
            )}
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            key={`${params.sortBy}-${params.desc}-${params.page}-${data?.records.length}`}
            ref={dataGridRef}
            dataSource={data?.records}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: numberOfRows }}
            noDataText={''}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={x.dataField}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {!!numberOfRows && !isExternalServiceUnavailable && (
          <AppPagination {...paginationProps} />
        )}
      </Loading>
    </div>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.DeviceOverview'>,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'deviceNumber',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('device_number'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 140,
  },
  {
    dataField: 'description',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('description'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 280,
  },
  {
    dataField: 'placeOfLocation',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('place_of_location'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 280,
  },
  {
    dataField: 'yearOfManufacture',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('year_of_manufacture'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 140,
  },
  {
    dataField: 'nextMaintenance',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('next_maintenance'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 150,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'typeOfMaintenance',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('type_of_maintenance_in_years'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    alignment: 'left',
    width: 180,
  },
  {
    dataField: 'orderNumber',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('order_number'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    alignment: 'left',
    width: 150,
  },
  {
    dataField: 'shippingDate',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('shipping_date'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 150,
    cellRender: (cell) => formatDate(cell.value),
  },
];
